import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Toolbar,
  Collapse,
  Box,
  Divider,
  Typography,
  useTheme,
  ListItemButton
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ExpandLess,
  ExpandMore,
  ExitToApp as ExitToAppIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import './Menu.css';

function Menu({ menuOptions, open, onToggle, isMobile = false, drawerWidth = 240 }) {
  const [openSubmenu, setOpenSubmenu] = React.useState({});
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useAuth();

  const handleToggleSubmenu = (text) => {
    setOpenSubmenu((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const handleNavigation = (path, onClick) => {
    navigate(path);
    // Ejecutar callback adicional si existe (para cerrar menú en móvil)
    if (onClick) onClick();
    // En móvil, cerrar el drawer después de navegar
    if (isMobile && onToggle) {
      onToggle();
    }
  };

  const handleLogout = () => {
    console.log('🔴 Botón de logout presionado');
    // Cerrar menú si está en móvil
    if (isMobile && onToggle) {
      onToggle();
    }
    // Llamar a la función logout del contexto que limpia TODO
    logout(true);
  };

  const drawerContent = (
    <Box
      className="menu-drawer-content"
      sx={{
        height: '100%',
        background: 'linear-gradient(180deg, #1A237E 0%, #283593 50%, #3949AB 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header del menú */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        minHeight: 64,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold',
          background: 'linear-gradient(45deg, #4FC3F7, #29B6F6)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          TRACKEA
        </Typography>
        <IconButton 
          onClick={onToggle}
          sx={{ 
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          {isMobile ? <CloseIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      {/* Espacio para compensar la AppBar solo en desktop */}
      {!isMobile && <Toolbar />}

      {/* Lista de menú */}
      <List
        sx={{
          flexGrow: 1,
          py: 1,
          background: 'transparent !important',
          backgroundColor: 'transparent !important',
          backgroundImage: 'none !important',
          '& .MuiListItem-root': {
            borderRadius: '0 25px 25px 0',
            mx: 1,
            my: 0.5,
            transition: 'all 0.3s ease',
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
              transform: 'translateX(5px)'
            }
          },
          '& .MuiListItemButton-root': {
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
            }
          },
          '& .MuiListItemIcon-root': {
            color: '#4FC3F7',
            minWidth: 40
          },
          '& .MuiListItemText-primary': {
            fontSize: { xs: '0.9rem', sm: '1rem' },
            fontWeight: 500
          }
        }}
      >
        {menuOptions.map(({ text, icon, path, subOptions, onClick }) => (
          <React.Fragment key={text}>
            <ListItemButton
              onClick={() => {
                if (subOptions) {
                  handleToggleSubmenu(text);
                } else {
                  handleNavigation(path, onClick);
                }
              }}
              sx={{
                pl: 2,
                pr: 1,
                py: 1.5,
                backgroundColor: 'transparent !important',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(79, 195, 247, 0.2) !important',
                  '&:hover': {
                    backgroundColor: 'rgba(79, 195, 247, 0.3) !important',
                  }
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {icon}
              </ListItemIcon>
              <ListItemText 
                primary={text}
                primaryTypographyProps={{
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  fontWeight: 500
                }}
              />
              {subOptions && (
                <Box sx={{ color: '#4FC3F7' }}>
                  {openSubmenu[text] ? <ExpandLess /> : <ExpandMore />}
                </Box>
              )}
            </ListItemButton>

            {/* Submenús */}
            {subOptions && (
              <Collapse
                in={openSubmenu[text]}
                timeout="auto"
                unmountOnExit
                sx={{
                  backgroundColor: 'transparent !important',
                  '& .MuiCollapse-wrapper': {
                    backgroundColor: 'transparent !important'
                  },
                  '& .MuiCollapse-wrapperInner': {
                    backgroundColor: 'transparent !important'
                  }
                }}
              >
                <List
                  component="div"
                  disablePadding
                  sx={{
                    backgroundColor: 'transparent !important',
                    background: 'none !important'
                  }}
                >
                  {subOptions.map((subOption) => (
                    <ListItemButton
                      key={subOption.text}
                      className="submenu-item"
                      sx={{
                        pl: 6,
                        pr: 2,
                        py: 1,
                        ml: 1,
                        mr: 2,
                        borderRadius: '0 20px 20px 0',
                        '&:hover': {
                          transform: 'translateX(3px)'
                        }
                      }}
                      onClick={() => handleNavigation(subOption.path, subOption.onClick)}
                    >
                      {subOption.icon && (
                        <ListItemIcon sx={{ 
                          minWidth: 32,
                          color: '#4FC3F7',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.2rem'
                          }
                        }}>
                          {subOption.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText 
                        primary={subOption.text}
                        primaryTypographyProps={{
                          fontSize: { xs: '0.8rem', sm: '0.85rem' },
                          fontWeight: 400
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Footer del menú con logout */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ 
          borderColor: 'rgba(255, 255, 255, 0.1)',
          mx: 2 
        }} />
        <ListItemButton 
          onClick={handleLogout}
          sx={{
            mx: 1,
            my: 1,
            borderRadius: '0 25px 25px 0',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              transform: 'translateX(5px)',
              '& .MuiListItemIcon-root': {
                color: '#f44336'
              },
              '& .MuiListItemText-primary': {
                color: '#f44336'
              }
            }
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 40,
            color: '#ff8a80'
          }}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Cerrar Sesión"
            primaryTypographyProps={{
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontWeight: 500
            }}
          />
        </ListItemButton>

        {/* Información adicional en el footer */}
        <Box sx={{ 
          p: 2, 
          textAlign: 'center',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <Typography variant="caption" sx={{ 
            opacity: 0.7,
            fontSize: '0.7rem'
          }}>
            © 2024 TRACKEA
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: isMobile 
            ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
            : '0 4px 20px rgba(26, 35, 126, 0.15)',
          ...(isMobile && {
            position: 'fixed',
            height: '100%',
            zIndex: theme.zIndex.drawer + 2
          })
        },
      }}
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={open}
      onClose={isMobile ? onToggle : undefined}
      ModalProps={isMobile ? {
        keepMounted: true, // Better open performance on mobile
      } : {}}
    >
      {drawerContent}
    </Drawer>
  );
}

export default Menu;